import { isGlobalViewer } from '../../../constants/roles';
// src/modules/goldVault/pages/TransferDetail.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Tag, Button, Typography, Spin, Row, Col, Steps,
  Table, Modal, Form, Input, InputNumber, Space, message, Alert, Image,
  Upload, Tooltip, Badge,
} from 'antd';
import {
  CheckCircleOutlined, ArrowLeftOutlined, FilePdfOutlined,
  CameraOutlined, DeleteOutlined, PlusOutlined, LoadingOutlined,
  ClockCircleOutlined, RollbackOutlined, CloseCircleOutlined,
  UserOutlined, ScissorOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getTransferById,
  approveTransfer, packTransfer, dispatchTransfer, withdrawTransfer,
  receiveTransfer, verifyTransfer, closeTransfer,
  downloadTransferDocument, addPackagePhotos, removePackagePhoto,
  getTransferApproval, decideTransferApproval, sendBackTransferStep,
} from '../api';
import API from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById } from '../../branches/api';
import PhoneCaptureButton from '../../../components/PhoneCapture/PhoneCaptureButton';
import ImageCropModal from '../../../components/ImageEditor/ImageCropModal';
import { headerSafeFileName } from '../../../utils/headerSafeFileName';

const { Title, Text } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

const STATUS_STEPS = ['CREATED', 'APPROVED', 'PACKED', 'DISPATCHED', 'IN_TRANSIT', 'RECEIVED', 'VERIFIED', 'CLOSED'];
const STATUS_COLOR = {
  CREATED: 'default', APPROVED: 'blue', PACKED: 'cyan', DISPATCHED: 'orange',
  IN_TRANSIT: 'gold', RECEIVED: 'lime', VERIFIED: 'green', CLOSED: 'default',
  REJECTED: 'red', CANCELLED: 'default',
};

export default function TransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messageApi, ctx] = message.useMessage();

  const [transfer, setTransfer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [approvalData, setApprovalData] = useState(null);
  const [isHO, setIsHO] = useState(false);

  const [packModal, setPackModal] = useState(false);
  const [receiveModal, setReceiveModal] = useState(false);
  const [photoModal, setPhotoModal] = useState(null); // packageId or null
  const [croppingPhoto, setCroppingPhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [packForm] = Form.useForm();
  const [receiveForm] = Form.useForm();
  const [approvalForm] = Form.useForm();
  const [approvalModal, setApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approved');
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawForm] = Form.useForm();
  const [sendBackModal, setSendBackModal] = useState(false);
  const [sendBackForm] = Form.useForm();
  const fileInputRef = useRef(null);

  useEffect(() => { load(); }, [id]);

  // Detect HO user — same pattern used across case lists
  useEffect(() => {
    const detectHO = async () => {
      if (!user) return;
      if (isGlobalViewer(user)) { setIsHO(true); return; }
      if (!user.branch) return;
      try {
        const res = await fetchBranchById(user.branch);
        const branch = res?.data || res;
        if (branch?.branchType === 'HO') setIsHO(true);
      } catch (_) {}
    };
    detectHO();
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getTransferById(id);
      setTransfer(res.data);
      setPackages(res.data.packages || []);

      try {
        const appRes = await getTransferApproval(id);
        setApprovalData(appRes.data);
      } catch (err) {
        console.warn('Could not fetch approval data', err);
      }
    } catch {
      messageApi.error('Failed to load transfer');
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (actionFn, ...args) => {
    setActionLoading(true);
    try {
      await actionFn(id, ...args);
      messageApi.success('Action completed');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePack = async () => {
    const values = await packForm.validateFields();
    await doAction(packTransfer, { packages: [values] });
    setPackModal(false);
    packForm.resetFields();
  };

  const handleReceive = async () => {
    const values = await receiveForm.validateFields();
    await doAction(receiveTransfer, {
      receivedBy: null,
      receivedGrossWeight: values.receivedGrossWeight,
      receivedNetWeight: values.receivedNetWeight,
      businessDate: new Date().toISOString(),
    });
    setReceiveModal(false);
    receiveForm.resetFields();
  };

  const handleApprovalDecision = async () => {
    try {
      const values = await approvalForm.validateFields();
      setActionLoading(true);
      await decideTransferApproval(id, { action: approvalAction, comment: values.comment || '' });
      messageApi.success(`Transfer ${approvalAction}`);
      setApprovalModal(false);
      load();
    } catch (e) {
      if (e.errorFields) return; // Form validation failed
      messageApi.error(e.response?.data?.message || `Failed to ${approvalAction}`);
    } finally {
      setActionLoading(false);
    }
  };

  const openApprovalModal = (action) => {
    setApprovalAction(action);
    approvalForm.resetFields();
    setApprovalModal(true);
  };

  const handleWithdraw = async () => {
    try {
      const values = await withdrawForm.validateFields();
      setActionLoading(true);
      await withdrawTransfer(id, { remarks: values.remarks });
      messageApi.success('Transfer withdrawn successfully');
      setWithdrawModal(false);
      load();
    } catch (e) {
      if (e.errorFields) return;
      messageApi.error(e.response?.data?.message || 'Failed to withdraw transfer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendBack = async () => {
    try {
      const values = await sendBackForm.validateFields();
      setActionLoading(true);
      await sendBackTransferStep(id, { comment: values.comment });
      messageApi.success('Transfer sent back to previous step');
      setSendBackModal(false);
      sendBackForm.resetFields();
      load();
    } catch (e) {
      if (e.errorFields) return;
      messageApi.error(e.response?.data?.message || 'Failed to send back');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      const blob = await downloadTransferDocument(id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transfer_Note_${transfer?.transferNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after the download manager has picked the URL up — revoking
      // synchronously can abort the download ("file wasn't available on site").
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to download transfer note');
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!photoModal) return;
    setPhotoUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const uploadRes = await API.post('/uploads/buffer', buffer, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-file-name': headerSafeFileName(file.name),
          'x-folder-path': '/transfers/delivery-photos',
        },
      });
      const fileRef = uploadRes.data.data;
      await addPackagePhotos(photoModal, [fileRef]);
      messageApi.success('Photo uploaded');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Phone-captured delivery photos are already on ImageKit; attach them to the
  // package directly instead of re-uploading.
  const handlePhonePhotos = async (incoming = []) => {
    if (!photoModal || incoming.length === 0) return;
    setPhotoUploading(true);
    try {
      await addPackagePhotos(photoModal, incoming.map((f) => ({
        fileId: f.fileId,
        url: f.url,
        thumbnailUrl: f.thumbnailUrl,
        filePath: f.filePath,
        name: f.name,
      })));
      messageApi.success(`${incoming.length} photo(s) received from phone`);
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to attach photos from phone');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Attach the cropped copy before dropping the original, so a failure never
  // leaves the package with no photo at all.
  const handleCroppedPhoto = async (photo, fileRef) => {
    setCroppingPhoto(null);
    if (!photoModal) return;
    setPhotoUploading(true);
    try {
      await addPackagePhotos(photoModal, [{
        fileId: fileRef.fileId,
        url: fileRef.url,
        thumbnailUrl: fileRef.thumbnailUrl,
        filePath: fileRef.filePath,
        name: fileRef.name,
      }]);
      await removePackagePhoto(photoModal, photo.fileId);
      messageApi.success('Photo updated');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to update the photo');
      load();
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRemovePhoto = async (packageId, fileId) => {
    try {
      await removePackagePhoto(packageId, fileId);
      messageApi.success('Photo removed');
      load();
    } catch {
      messageApi.error('Remove failed');
    }
  };

  const activePackage = packages.find((p) => p._id === photoModal);

  if (loading) return <Spin style={{ display: 'block', padding: 48 }} />;
  if (!transfer) return null;

  let STATUS_LIST = STATUS_STEPS;
  if (transfer.status === 'REJECTED' || transfer.status === 'CANCELLED') {
    STATUS_LIST = [...STATUS_STEPS.slice(0, 1), transfer.status];
  }
  const currentStep = STATUS_LIST.indexOf(transfer.status);

  const isCreator = user && transfer.createdBy && (
    (typeof transfer.createdBy === 'object' && transfer.createdBy._id === user._id) ||
    transfer.createdBy === user._id
  );
  const canWithdraw = isCreator && ['CREATED', 'APPROVED'].includes(transfer.status);

  const packageColumns = [
    { title: 'Package No.', dataIndex: 'packageNumber', key: 'pn', width: 130 },
    { title: 'Seal No.', dataIndex: 'sealNumber', key: 'sn', width: 120 },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'gw', width: 90, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'nw', width: 90, render: fmtW },
    { title: 'Items', dataIndex: 'itemCount', key: 'ic', width: 70 },
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 90,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Photos',
      key: 'photos',
      width: 90,
      render: (_, row) => (
        <Tooltip title="View / Add delivery photos">
          <Badge count={row.deliveryPhotos?.length || 0} size="small">
            <Button
              size="small"
              icon={<CameraOutlined />}
              onClick={() => setPhotoModal(row._id)}
            />
          </Badge>
        </Tooltip>
      ),
    },
  ];

  const caseColumns = [
    { title: 'Case No.', dataIndex: 'caseNumber', key: 'cn', width: 110 },
    { title: 'Type', dataIndex: 'sourceType', key: 'st', width: 80 },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'gw', width: 90, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'nw', width: 90, render: fmtW },
  ];

  return (
    <div style={{ padding: 24 }}>
      {ctx}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/vault/transfers')}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>Transfer: {transfer.transferNumber}</Title>
            <Tag color={STATUS_COLOR[transfer.status]}>{transfer.status}</Tag>
          </Space>
        </Col>
        <Col>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleDownloadDocument}
          >
            Download Transfer Note
          </Button>
        </Col>
      </Row>

      <Steps
        current={currentStep !== -1 ? currentStep : 0}
        size="small"
        style={{ marginBottom: 24 }}
        items={STATUS_LIST.map((s) => ({
          title: s,
          status: s === 'REJECTED' || s === 'CANCELLED' ? 'error' : undefined
        }))}
      />

      <Row gutter={16}>
        <Col xs={24} md={16}>
          <Card title="Transfer Details" size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Transfer No.">{transfer.transferNumber}</Descriptions.Item>
              <Descriptions.Item label="Business Date">{dayjs(transfer.businessDate).format('DD MMM YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Source Branch">{transfer.sourceBranch?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Destination">{transfer.destinationBranch?.name || 'HO'}</Descriptions.Item>
              <Descriptions.Item label="Gross Weight">{fmtW(transfer.grossWeight)}</Descriptions.Item>
              <Descriptions.Item label="Net Weight">{fmtW(transfer.netWeight)}</Descriptions.Item>
              <Descriptions.Item label="Cases">{transfer.caseCount}</Descriptions.Item>
              <Descriptions.Item label="Packages">{transfer.packageCount}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Chain of Custody" size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Created By">{transfer.createdBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Approved By">{transfer.approvedBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Packed By">{transfer.packedBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Verified By">{transfer.verifiedBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Received By">{transfer.receivedBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Checked By">{transfer.checkedBy?.name || '—'}</Descriptions.Item>
              {transfer.receivedGrossWeight != null && (
                <>
                  <Descriptions.Item label="Received Gross">{fmtW(transfer.receivedGrossWeight)}</Descriptions.Item>
                  <Descriptions.Item label="Received Net">{fmtW(transfer.receivedNetWeight)}</Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>

          {transfer.discrepancy && (
            <Alert
              type={transfer.discrepancy.status === 'MATCHED' ? 'success' : 'warning'}
              message={`Discrepancy: ${transfer.discrepancy.status}`}
              description={
                <div>
                  <div>Gross diff: {transfer.discrepancy.grossWeightDifference?.toFixed(4)}g</div>
                  <div>Net diff: {transfer.discrepancy.netWeightDifference?.toFixed(4)}g</div>
                  {transfer.discrepancy.investigationNotes && <div>Notes: {transfer.discrepancy.investigationNotes}</div>}
                </div>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {packages.length > 0 && (
            <Card title="Packages" size="small" style={{ marginBottom: 16 }}>
              <Table dataSource={packages} rowKey="_id" columns={packageColumns} size="small" pagination={false} />
            </Card>
          )}

          <Card title="Cases in Transfer" size="small">
            <Table dataSource={transfer.caseRefs} rowKey="caseNumber" columns={caseColumns} size="small" pagination={false} />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Actions" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Step 1 — Approval Workflow (via approval engine) */}
              {approvalData?.canAct && transfer.status === 'CREATED' && (
                <>
                  <Button block type="primary" loading={actionLoading} onClick={() => openApprovalModal('approved')}>
                    Approve Transfer
                  </Button>
                  <Button block danger loading={actionLoading} onClick={() => openApprovalModal('rejected')}>
                    Reject Transfer
                  </Button>
                </>
              )}

              {/* Step 2 — Pack (driven by approval instance) */}
              {transfer.status === 'APPROVED' && approvalData?.lifecycleSteps?.find(s => s.stepNumber === 2)?.canViewerAct && (
                <Button block type="primary" loading={actionLoading} onClick={() => setPackModal(true)}>
                  Pack & Seal
                </Button>
              )}

              {/* Step 3 — Dispatch (driven by approval instance) */}
              {transfer.status === 'PACKED' && approvalData?.lifecycleSteps?.find(s => s.stepNumber === 3)?.canViewerAct && (
                <Button block type="primary" loading={actionLoading}
                  onClick={() => doAction(dispatchTransfer, { remarks: '' })}>
                  Mark Dispatched
                </Button>
              )}

              {/* Step 4 — Receive (driven by approval instance) */}
              {['DISPATCHED', 'IN_TRANSIT'].includes(transfer.status) && approvalData?.lifecycleSteps?.find(s => s.stepNumber === 4)?.canViewerAct && (
                <Button block type="primary" loading={actionLoading} onClick={() => setReceiveModal(true)}>
                  Receive at HO
                </Button>
              )}

              {/* Step 5 — Verify (driven by approval instance) */}
              {transfer.status === 'RECEIVED' && approvalData?.lifecycleSteps?.find(s => s.stepNumber === 5)?.canViewerAct && (
                <Button block type="primary" loading={actionLoading}
                  onClick={() => doAction(verifyTransfer, { remarks: '' })}>
                  Verify at HO
                </Button>
              )}

              {/* Step 6 — Close (driven by approval instance) */}
              {transfer.status === 'VERIFIED' && approvalData?.lifecycleSteps?.find(s => s.stepNumber === 6)?.canViewerAct && (
                <Button block loading={actionLoading} onClick={() => doAction(closeTransfer)}>
                  Close Transfer
                </Button>
              )}

              {/* Send Back (available for steps 2-6 if user can act) */}
              {transfer.status !== 'CREATED' && !['REJECTED', 'CANCELLED', 'CLOSED'].includes(transfer.status) && approvalData?.canActOnLifecycle && (
                <Button block icon={<RollbackOutlined />} loading={actionLoading} onClick={() => setSendBackModal(true)}>
                  Send Back to Previous Step
                </Button>
              )}

              {/* Creator-only: Withdraw */}
              {canWithdraw && (
                <Button block danger loading={actionLoading} onClick={() => setWithdrawModal(true)}>
                  Withdraw Transfer
                </Button>
              )}

              {/* Fallback: show actions for legacy transfers without approval instance */}
              {!approvalData?.lifecycleSteps && (
                <>
                  {!isHO && transfer.status === 'APPROVED' && (
                    <Button block type="primary" loading={actionLoading} onClick={() => setPackModal(true)}>Pack & Seal</Button>
                  )}
                  {!isHO && transfer.status === 'PACKED' && (
                    <Button block type="primary" loading={actionLoading} onClick={() => doAction(dispatchTransfer, { remarks: '' })}>Mark Dispatched</Button>
                  )}
                  {isHO && ['DISPATCHED', 'IN_TRANSIT'].includes(transfer.status) && (
                    <Button block type="primary" loading={actionLoading} onClick={() => setReceiveModal(true)}>Receive at HO</Button>
                  )}
                  {isHO && transfer.status === 'RECEIVED' && (
                    <Button block type="primary" loading={actionLoading} onClick={() => doAction(verifyTransfer, { remarks: '' })}>Verify at HO</Button>
                  )}
                  {isHO && transfer.status === 'VERIFIED' && (
                    <Button block loading={actionLoading} onClick={() => doAction(closeTransfer)}>Close Transfer</Button>
                  )}
                </>
              )}

              <Button block icon={<FilePdfOutlined />} onClick={handleDownloadDocument}>
                Transfer Note PDF
              </Button>
            </Space>
          </Card>

          {/* Approval Chain Card */}
          {approvalData?.lifecycleSteps && approvalData.lifecycleSteps.length > 0 && (
            <Card title="Approval Chain" size="small" style={{ marginTop: 16 }}>
              <Steps
                direction="vertical"
                size="small"
                current={(() => {
                  const idx = approvalData.lifecycleSteps.findIndex(s => s.status === 'pending');
                  return idx === -1 ? approvalData.lifecycleSteps.length : idx;
                })()}
                items={approvalData.lifecycleSteps.map((step) => {
                  let status = 'wait';
                  let icon = <ClockCircleOutlined />;
                  if (step.status === 'approved') {
                    status = 'finish';
                    icon = <CheckCircleOutlined />;
                  } else if (step.status === 'rejected') {
                    status = 'error';
                    icon = <CloseCircleOutlined />;
                  } else if (step.canViewerAct) {
                    status = 'process';
                  }

                  const approverNames = (step.approvers || []).map(a => a.name).filter(Boolean).join(', ') || 'Not assigned';
                  const actedByInfo = (step.actedBy || []).map(a => (
                    `${a.user?.name || 'Unknown'} (${dayjs(a.timestamp).format('DD MMM HH:mm')})`
                  )).join(', ');

                  return {
                    title: (
                      <span>
                        {step.stepName}
                        <Tag color={step.status === 'approved' ? 'green' : step.status === 'rejected' ? 'red' : 'default'} style={{ marginLeft: 8, fontSize: 11 }}>
                          {step.targetStatus}
                        </Tag>
                      </span>
                    ),
                    description: (
                      <div style={{ fontSize: 12 }}>
                        {step.status === 'approved' && actedByInfo ? (
                          <span style={{ color: '#52c41a' }}>{actedByInfo}</span>
                        ) : (
                          <span><UserOutlined style={{ marginRight: 4 }} />{approverNames}</span>
                        )}
                      </div>
                    ),
                    status,
                    icon,
                  };
                })}
              />
            </Card>
          )}
        </Col>
      </Row>

      {/* Pack Modal */}
      <Modal
        title="Pack Transfer — Add Package"
        open={packModal}
        onCancel={() => setPackModal(false)}
        onOk={handlePack}
        okText="Pack"
        confirmLoading={actionLoading}
      >
        <Form form={packForm} layout="vertical">
          <Text type="secondary">A package number and seal number will be auto-generated.</Text>
          <Form.Item label="Gross Weight (g)" name="grossWeight" rules={[{ required: true }]} style={{ marginTop: 12 }}>
            <InputNumber min={0} precision={3} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Net Weight (g)" name="netWeight" rules={[{ required: true }]}>
            <InputNumber min={0} precision={3} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Item Count" name="itemCount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Withdraw Modal */}
      <Modal
        title="Withdraw Transfer"
        open={withdrawModal}
        onCancel={() => setWithdrawModal(false)}
        onOk={handleWithdraw}
        okText="Withdraw"
        okButtonProps={{ danger: true }}
        confirmLoading={actionLoading}
      >
        <Form form={withdrawForm} layout="vertical">
          <Alert message="Withdrawing this transfer will reverse the gold back into the branch vault." type="warning" style={{ marginBottom: 12 }} />
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} placeholder="Optional reason for withdrawal" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Receive Modal */}
      <Modal
        title="Receive Transfer at HO"
        open={receiveModal}
        onCancel={() => setReceiveModal(false)}
        onOk={handleReceive}
        okText="Confirm Receipt"
        confirmLoading={actionLoading}
      >
        <Form form={receiveForm} layout="vertical">
          <Alert message="Verify seal condition and weigh the received gold before proceeding." type="info" style={{ marginBottom: 12 }} />
          <Form.Item label="Received Gross Weight (g)" name="receivedGrossWeight" rules={[{ required: true }]}>
            <InputNumber min={0} precision={3} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Received Net Weight (g)" name="receivedNetWeight" rules={[{ required: true }]}>
            <InputNumber min={0} precision={3} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Approval Modal */}
      <Modal
        title={approvalAction === 'approved' ? 'Approve Transfer' : 'Reject Transfer'}
        open={approvalModal}
        onCancel={() => setApprovalModal(false)}
        onOk={handleApprovalDecision}
        okText={approvalAction === 'approved' ? 'Approve' : 'Reject'}
        okButtonProps={{ danger: approvalAction === 'rejected' }}
        confirmLoading={actionLoading}
      >
        <Form form={approvalForm} layout="vertical">
          {approvalAction === 'rejected' && (
            <Alert message="Inventory will be reversed to the source branch." type="warning" style={{ marginBottom: 12 }} />
          )}
          <Form.Item
            label="Comments"
            name="comment"
            rules={[{ required: approvalAction === 'rejected', message: 'A comment is required when rejecting.' }]}
          >
            <Input.TextArea rows={3} placeholder={approvalAction === 'rejected' ? 'Reason for rejection...' : 'Optional remarks...'} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Send Back Modal */}
      <Modal
        title="Send Back to Previous Step"
        open={sendBackModal}
        onCancel={() => setSendBackModal(false)}
        onOk={handleSendBack}
        okText="Send Back"
        okButtonProps={{ icon: <RollbackOutlined /> }}
        confirmLoading={actionLoading}
      >
        <Form form={sendBackForm} layout="vertical">
          <Alert message="This will revert the transfer to the previous status. The approver for that step will need to act again." type="warning" style={{ marginBottom: 12 }} />
          <Form.Item
            label="Reason for sending back"
            name="comment"
            rules={[{ required: true, message: 'A comment is required when sending back.' }]}
          >
            <Input.TextArea rows={3} placeholder="Explain why this step needs to be redone..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Package Delivery Photos Modal */}
      <Modal
        title={`Delivery Photos — ${activePackage?.packageNumber || ''}`}
        open={!!photoModal}
        onCancel={() => setPhotoModal(null)}
        footer={null}
        width={640}
      >
        {activePackage && (
          <div>
            <ImageCropModal
              open={Boolean(croppingPhoto)}
              image={croppingPhoto}
              folderPath="/transfers/delivery-photos"
              onCancel={() => setCroppingPhoto(null)}
              onCropped={(fileRef) => handleCroppedPhoto(croppingPhoto, fileRef)}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              Seal: {activePackage.sealNumber} &nbsp;|&nbsp;
              Gross: {fmtW(activePackage.grossWeight)} &nbsp;|&nbsp;
              Status: {activePackage.status}
            </Text>

            {/* Existing photos */}
            {activePackage.deliveryPhotos?.length > 0 ? (
              <Image.PreviewGroup>
                <Row gutter={8} style={{ marginBottom: 16 }}>
                  {activePackage.deliveryPhotos.map((photo) => (
                    <Col key={photo.fileId}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Image
                          src={photo.thumbnailUrl || photo.url}
                          width={100}
                          height={100}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ position: 'absolute', top: 2, right: 2, opacity: 0.85 }}
                          onClick={() => handleRemovePhoto(activePackage._id, photo.fileId)}
                        />
                        <Tooltip title="Crop / reposition">
                          <Button
                            size="small"
                            icon={<ScissorOutlined />}
                            style={{ position: 'absolute', bottom: 2, right: 2, opacity: 0.85 }}
                            onClick={() => setCroppingPhoto(photo)}
                            disabled={photoUploading}
                          />
                        </Tooltip>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            ) : (
              <Alert message="No delivery photos yet" type="info" style={{ marginBottom: 12 }} />
            )}

            {/* Upload new photo */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePhotoUpload(file);
                  e.target.value = '';
                }
              }}
            />
            <Button
              icon={photoUploading ? <LoadingOutlined /> : <PlusOutlined />}
              loading={photoUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Photo
            </Button>
            <PhoneCaptureButton
              targetKey="goldVault.deliveryPhoto"
              targetLabel="Package Delivery Photos"
              folderPath="/transfers/delivery-photos"
              recordId={photoModal || undefined}
              maxFiles={10}
              onFiles={handlePhonePhotos}
              disabled={photoUploading}
              buttonText="Capture from Phone (QR)"
              buttonProps={{ style: { marginLeft: 8 } }}
            />
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              Max 10MB. JPG, PNG, WEBP accepted.
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
}
